import React, { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  FileText,
  Plus,
  X,
  Trash2,
} from "lucide-react";
import { toast } from "react-toastify";

const MAX_DESCRIPTION_LENGTH = 200;

/**
 * Event Itinerary Section Component
 * Similar to EventScheduleSection - Dynamic list for itinerary items
 */
const EventItinerarySection = ({
  value = [],
  onChange,
  className = "",
}) => {
  const [itineraryItems, setItineraryItems] = useState(value || []);
  const [newItinerary, setNewItinerary] = useState({
    title: "",
    time: "",
    description: "",
  });

  useEffect(() => {
    if (value && Array.isArray(value)) {
      setItineraryItems(value);
    }
  }, [value]);

  const handleInputChange = (field, value) => {
    if (field === "description" && value.length > MAX_DESCRIPTION_LENGTH) {
      toast.warning(`Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`);
      return;
    }
    setNewItinerary((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddItinerary = () => {
    const trimmedTitle = newItinerary.title.trim();
    const trimmedDescription = newItinerary.description.trim();

    if (!trimmedTitle) {
      toast.error("Please enter an itinerary title");
      return;
    }

    if (!newItinerary.time) {
      toast.error("Please select an itinerary time");
      return;
    }

    if (!trimmedDescription) {
      toast.error("Please enter an itinerary description");
      return;
    }

    if (trimmedDescription.length > MAX_DESCRIPTION_LENGTH) {
      toast.error(`Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`);
      return;
    }

    const newItem = {
      title: trimmedTitle,
      time: newItinerary.time,
      description: trimmedDescription,
    };

    const updatedItinerary = [...itineraryItems, newItem];
    setItineraryItems(updatedItinerary);
    onChange(updatedItinerary);
    setNewItinerary({
      title: "",
      time: "",
      description: "",
    });
    toast.success("Itinerary item added");
  };

  const handleRemoveItinerary = (index) => {
    const updatedItinerary = itineraryItems.filter((_, i) => i !== index);
    setItineraryItems(updatedItinerary);
    onChange(updatedItinerary);
    toast.success("Itinerary item removed");
  };

  return (
    <div className={className}>
      <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="flex items-center mb-4 space-x-2">
          <Calendar className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Event Itinerary</h3>
          <span className="px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full">
            {itineraryItems.length}
          </span>
        </div>

        <p className="mb-4 text-xs text-gray-600">
          Add itinerary items for your event schedule. Each item includes a title,
          time, and short description (max {MAX_DESCRIPTION_LENGTH} characters).
          You can add multiple itinerary items.
        </p>

        {/* Add Itinerary Form */}
        <div className="p-4 mb-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-3">
            {/* Itinerary Title */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Itinerary Title <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  type="text"
                  value={newItinerary.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="e.g., Opening Ceremony, Keynote Speech"
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white"
                />
              </div>
            </div>

            {/* Itinerary Time */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Itinerary Time <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  type="time"
                  value={newItinerary.time}
                  onChange={(e) => handleInputChange("time", e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white"
                />
              </div>
            </div>

            {/* Itinerary Description */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-gray-700">
                Short Description <span className="text-red-500">*</span>
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Max {MAX_DESCRIPTION_LENGTH} characters)
                </span>
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea
                  value={newItinerary.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Brief description of this itinerary item..."
                  rows={3}
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white resize-y"
                />
              </div>
              <div className="flex justify-between items-center mt-1">
                <p
                  className={`text-xs ${
                    newItinerary.description.length > MAX_DESCRIPTION_LENGTH * 0.9
                      ? "text-orange-600"
                      : newItinerary.description.length === MAX_DESCRIPTION_LENGTH
                      ? "text-red-600"
                      : "text-gray-500"
                  }`}
                >
                  {newItinerary.description.length}/{MAX_DESCRIPTION_LENGTH} characters
                </p>
              </div>
            </div>

            {/* Add Button */}
            <button
              type="button"
              onClick={handleAddItinerary}
              disabled={
                !newItinerary.title.trim() ||
                !newItinerary.time ||
                !newItinerary.description.trim()
              }
              className="flex items-center justify-center space-x-2 px-4 py-2.5 w-full text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Add Itinerary Item</span>
            </button>
          </div>
        </div>

        {/* Itinerary Items List */}
        {itineraryItems.length > 0 ? (
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-700">
              Itinerary Items ({itineraryItems.length}):
            </p>
            <div className="space-y-3">
              {itineraryItems.map((item, index) => (
                <div
                  key={index}
                  className="p-4 bg-purple-50 border border-purple-200 rounded-lg group hover:bg-purple-100 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-purple-600 rounded-full">
                          <span className="text-xs font-bold text-white">
                            {index + 1}
                          </span>
                        </div>
                        <h4 className="text-sm font-bold text-gray-900">
                          {item.title}
                        </h4>
                      </div>
                      <div className="flex items-center space-x-4 ml-10 mb-2">
                        <div className="flex items-center space-x-1.5 text-xs text-gray-600">
                          <Clock className="w-3.5 h-3.5 text-purple-600" />
                          <span className="font-medium">{item.time}</span>
                        </div>
                      </div>
                      <p className="ml-10 text-xs text-gray-700 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveItinerary(index)}
                      className="flex-shrink-0 p-2 ml-3 text-red-600 bg-white rounded-lg border border-red-200 transition-colors hover:bg-red-50 hover:border-red-300"
                      title="Remove itinerary item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-sm font-medium text-gray-600">
              No itinerary items added yet
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Start adding itinerary items using the form above
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventItinerarySection;

